/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/subscriptionStatsTable.scss';

import {AwesomeIcon, ChangeFunction, CloseButton, Loader, SelectItemShape, WithBackground} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import React, {MouseEventHandler, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import SubscriptionStats from '../../../models/subscriptionStatsModel';

import {DataSplitMethod} from '../../../util/interfaces';

import {BolmateSelect} from '../../controls';

interface Props {
    onClose: MouseEventHandler;
    onChangePage: (page: number) => MouseEventHandler;
    onChangeSplitMethod: ChangeFunction<DataSplitMethod>;
    stats: SubscriptionStats[];
    loading: boolean;
    active: boolean;
    page: number;
    total: number;
    splitMethod: DataSplitMethod;
}

export default function SubscriptionStatsTable(props: Props) {
    const {stats, loading, page, active, total, onClose, onChangePage, onChangeSplitMethod, splitMethod} = props,
        {t} = useTranslation(),
        hasMore = (page + 1) * 20 < total,
        hasLess = page > 0;
    const splitMethodItems: SelectItemShape[] = useMemo(
        () => [
            {value: DataSplitMethod.DAY, label: t('subscription.stats.perDay', 'Per dag')},
            {value: DataSplitMethod.WEEK, label: t('subscription.stats.perWeek', 'Per week')},
            {value: DataSplitMethod.MONTH, label: t('subscription.stats.perMonth', 'Per maand')},
            {value: DataSplitMethod.QUARTER, label: t('subscription.stats.perQuarter', 'Per kwartaal')},
            {value: DataSplitMethod.YEAR, label: t('subscription.stats.perYear', 'Per jaar')}
        ],
        [t]
    );

    function renderStat(current: number, gain: number, positiveIsRed: boolean = false) {
        const token = gain >= 0 ? '+' : '',
            color = gain === 0 ? null : gain > 0 ? (positiveIsRed ? 'red' : 'green') : positiveIsRed ? 'green' : 'red';
        return (
            <label>
                {current}{' '}
                <span className={classNames('gain', color)}>
                    {token}
                    {gain}
                </span>
            </label>
        );
    }

    let dateLabel: string;
    switch (splitMethod) {
        case DataSplitMethod.DAY:
        case DataSplitMethod.HOUR:
            dateLabel = t('subscription.stats.day', 'Dag');
            break;
        case DataSplitMethod.MONTH:
            dateLabel = t('subscription.stats.month', 'Maand');
            break;
        case DataSplitMethod.QUARTER:
            dateLabel = t('subscription.stats.quarter', 'Kwartaal');
            break;
        case DataSplitMethod.WEEK:
            dateLabel = t('subscription.stats.week', 'Week');
            break;
        case DataSplitMethod.YEAR:
            dateLabel = t('subscription.stats.year', 'Jaar');
            break;
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='subscription-stats-table default-popup'>
                <div className='header-container'>
                    <label className='header-title'>{t('subscription.stats.title', 'Abonnementstatussen')}</label>
                    <CloseButton onClick={onClose} />
                </div>
                <div className='body-container'>
                    <div className='list-header'>
                        <BolmateSelect
                            value={splitMethod}
                            onChange={onChangeSplitMethod}
                            items={splitMethodItems}
                            label={t('subscription.stats.display', 'Weergave')}
                            className='display-select'
                        />
                    </div>
                    <div className='list'>
                        <Loader active={loading} />
                        <div className='row head'>
                            <label>{dateLabel}</label>
                            <label>{t('subscription.stats.active', 'Actief')}</label>
                            <label>{t('subscription.stats.trial', 'Proefperiode')}</label>
                            <label>{t('subscription.stats.free', 'Gratis')}</label>
                            <label>{t('subscription.stats.canceled', 'Opgezegd')}</label>
                            <label>{t('subscription.stats.expired', 'Proef verlopen')}</label>
                            <label>{t('subscription.stats.inactive', 'Inactief')}</label>
                        </div>
                        {stats.map((stat: SubscriptionStats, index: number) => {
                            const {
                                simpleStats,
                                activeGain,
                                inactiveGain,
                                freeGain,
                                trialGain,
                                canceledGain,
                                expiredGain
                            } = stat;
                            return (
                                <div className='row' key={index}>
                                    <label>{stat.periodLabel}</label>
                                    {renderStat(simpleStats.active, activeGain)}
                                    {renderStat(simpleStats.trial, trialGain)}
                                    {renderStat(simpleStats.free, freeGain)}
                                    {renderStat(simpleStats.canceled, canceledGain, true)}
                                    {renderStat(simpleStats.expired, expiredGain, true)}
                                    {renderStat(simpleStats.inactive, inactiveGain, true)}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className='footer-container'>
                    <label className='page-label'>
                        {t('subscription.stats.page', 'Pagina')} {page + 1}
                    </label>
                    <div className='pager-container'>
                        <AwesomeIcon
                            name='chevron-left'
                            className={classNames('pager', !hasLess && 'disabled')}
                            onClick={hasLess ? onChangePage(page - 1) : undefined}
                        />
                        <AwesomeIcon
                            name='chevron-right'
                            className={classNames('pager', !hasMore && 'disabled')}
                            onClick={hasMore ? onChangePage(page + 1) : undefined}
                        />
                    </div>
                </div>
            </div>
        </WithBackground>
    );
}
